/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import { dateToFormat } from 'libs/utils/time';

/**
 * @typedef {Object} RecordingMetadata
 * @property {Number} start_time
 * @property {Boolean} disable_transcription
 * @property {Number} chunck_index
 */

/**
 * @typedef {Object} Attachment
 * @property {String} content_type
 * @property {Boolean} transcription_requested
 * @property {RecordingMetadata} recording
 */

const __vue_script__ = {
    name: 'VideoCallRecordings',
    props: {
        event: {
            type: Object,
            required: true
        },

        videoCallDoc: {
            type: Object,
            required: true
        },

        baseName: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            listExpanded: false,
            isSpotMeUser: this.$services.user.matchesRoles({ globalRole: 'spotme' })
        };
    },

    computed: {

        /**
         * @returns {Record<String, Attachment>}
         */
        attachments() {
            return this.videoCallDoc?.fp_attachments_s3 || {};
        },

        /**
         * @returns {[string, Attachment][]}
         */
        mp4AttachmentEntries() {
            return Object.entries(this.attachments)
                .filter(([, a]) => a?.content_type === 'video/mp4')
                .sort(([nameA, a], [nameB, b]) => {
                    const tsA = a?.recording?.start_time ?? a?.timestamp ?? 0;
                    const tsB = b?.recording?.start_time ?? b?.timestamp ?? 0;

                    if (tsA !== tsB) return tsA - tsB; // 1) Sort chronologically (last recording session first)

                    const indexA = a?.recording?.chunk_index;
                    const indexB = b?.recording?.chunk_index;

                    if (indexA && indexB && indexA !== indexB) {
                        return indexA - indexB; // 2) Sort ascendingly (first chapter first)
                    }

                    return nameA.localeCompare(nameB); // 3) Sort alphabetically (first chapter sub-segment first)
                });
        },

        /**
         * @returns {Array<{
         *   filenameWithExt: String,
         *   filenameNoExt: String,
         *   transcriptionRequested: Boolean,
         *   timestamp: Number,
         *   disableTranscription: Boolean,
         * }>}
         */
        chapterRecordingGroups() {
            const entries = this.mp4AttachmentEntries.filter(([, attachment]) => !this.isLegacyRecording(attachment));

            const timestamps = [
                ...new Set(
                    entries.map(([, attachment]) => attachment.recording.start_time)
                )
            ];

            const groups = [];

            for (const timestamp of timestamps) {
                const attachments = entries.filter(([, attachment]) => attachment.recording.start_time === timestamp);

                const chapters = attachments
                    .map(([name, attachment]) => {
                        return {
                            filenameWithExt: name,
                            filenameNoExt: this.removeExt(name),
                            transcriptionRequested: attachment.transcription_requested,
                            timestamp: attachment.recording.start_time,
                            disableTranscription: attachment.recording.disable_transcription,
                        }; })
                    .filter(chapter =>
                        chapter.filenameNoExt.slice(0, -3).endsWith('-segment')
                        || !attachments.some(
                        // we remove parts that had to be cut for transcription, and show only new subparts
                        ([name]) => name !== chapter.filenameWithExt && name?.startsWith(chapter.filenameNoExt)
                    ));

                groups.push({
                    timestamp,
                    chapters
                });
            }

            return groups;
        },

        legacyRecordings() {
            const legacy = [];

            for (const [attachmentName, attachment] of this.mp4AttachmentEntries) {
                if (!this.isLegacyRecording(attachment)) {
                    continue;
                }

                const timestamp = attachment?.timestamp || 0;
                legacy.push({
                    filenameWithExt: attachmentName,
                    filenameNoExt: this.removeExt(attachmentName),
                    transcriptionRequested: attachment.transcription_requested,
                    timestamp
                });
            }

            return legacy.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        },

        recordings() {
            if (!this.videoCallDoc) {
                return [];
            }

            const recordings = [];
            let chapterDisplayIndex;

            for (const group of this.chapterRecordingGroups) {
                const { timestamp, chapters } = group;

                // If a chapter is too long for AWS to transcript, the lambda will first flag it as disable_transcription
                // then cut it in subparts, then remove the flag. In the meantime we hide that recording.
                const disabled = chapters.reduce((acc, chapter) => acc || chapter.disableTranscription, false);

                if (!chapters?.length || disabled) {
                    continue;
                }

                const displayName = this.getRecordingDisplayName(timestamp);

                if (chapters.length === 1) {
                    recordings.push(this.buildRecording(chapters[0], displayName));
                    continue;
                }

                const groupChapters = [];

                chapterDisplayIndex = 1;
                for (const chapter of chapters) {
                    const chapterName = `Chapter #${chapterDisplayIndex++} ${displayName}`;
                    const chapterRecording = this.buildRecording(chapter, chapterName);
                    groupChapters.push(chapterRecording);
                }

                recordings.push({
                    id: `group-${timestamp}`,
                    name: displayName,
                    hint: null,
                    chapters: groupChapters,
                    timestamp
                });
            }

            for (const record of this.legacyRecordings) {
                const ts = record.timestamp || 0;
                const displayName = this.getRecordingDisplayName(ts);

                recordings.push(this.buildRecording(record, displayName));
            }

            let maxFinishedTimestamp = 0;
            for (const rec of recordings) {
                const ts = rec.timestamp;
                if (typeof ts === 'number' && ts > maxFinishedTimestamp) {
                    maxFinishedTimestamp = ts;
                }
            }

            const calls = this.videoCallDoc.fp_calls || [];

            let latestUnfinishedRecording;
            for (const call of calls) {
                if (!call?.startRecordingTimestamp || call?.error) {
                    continue;
                }

                if (!maxFinishedTimestamp || call.startRecordingTimestamp > maxFinishedTimestamp) {
                    const startRecordingTimestamp = call.startRecordingTimestamp;
                    const displayName = this.getRecordingDisplayName(startRecordingTimestamp);

                    latestUnfinishedRecording = {
                        id: call.callId,
                        name: this.$i18n.t('generic_form.video.recordings.in_progress_name_prefix') + displayName,
                        hint: this.$i18n.t('generic_form.video.recordings.in_progress_hint'),
                        downloadUrl: null,
                        vttDownloadUrl: null,
                        transcriptionRequested: false,
                        timestamp: startRecordingTimestamp
                    };
                }
            }

            if (latestUnfinishedRecording) {
                recordings.push(latestUnfinishedRecording);
            }

            recordings.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

            return recordings;
        }
    },

    methods: {
        removeExt(name){
                return name?.replace(/\.mp4$/, '');
        },

        isLegacyRecording(attachment) {
            return !attachment?.recording?.start_time;
        },

        getRecordingDisplayName(timestamp) {
            if (typeof timestamp === 'number' && timestamp > 0) {
                return `${this.baseName}_${dateToFormat(timestamp * 1000, 'YYYY-MM-DD_HH-mm-ss')}`;
            }

            return this.baseName;
        },

        async transcribeClicked(recording) {
            await this.$services.videoCalls.requestMp4Transcription({
                eventId: this.event._id,
                docId: this.videoCallDoc._id,
                attachmentName: recording.filenameWithExt
            }).catch((err) => {
                // do not throw here, if the UI does not reflect the state for some reason, the refresh from the emit below might help
                console.error('Error requesting transcription:', err);
            });
            this.$emit('transcription-requested');
        },

        buildRecording({ filenameWithExt, filenameNoExt, transcriptionRequested, timestamp }, displayName) {

            const transcriptName = Object.keys(this.attachments || {}).find(
                key => key.startsWith(filenameNoExt) && key.endsWith('.vtt')
            );

            let vttDownloadUrl;
            if (transcriptName) {
                vttDownloadUrl = this.$services.videoCalls.getDownloadUrl({
                    eventId: this.event._id,
                    docId: this.videoCallDoc._id,
                    fileName: transcriptName,
                    downloadAs: `${displayName}.vtt`
                });
            }

            const downloadUrl = this.$services.videoCalls.getDownloadUrl({
                eventId: this.event._id,
                docId: this.videoCallDoc._id,
                fileName: filenameWithExt,
                downloadAs: `${displayName}.mp4`
            });

            return {
                id: filenameWithExt,
                filenameWithExt,
                filenameNoExt,
                name: displayName,
                hint: null,
                downloadUrl,
                vttDownloadUrl,
                transcriptionRequested,
                timestamp
            };
        }
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.recordings && _vm.recordings.length)?_c('div',{staticClass:"video-call-recordings"},[_c('div',{staticClass:"alert alert-warning"},[_c('p',[_vm._v(_vm._s(_vm.$t('generic_form.video.recordings.transcript_hint')))])]),_c('div',{staticClass:"existing-recordings"},_vm._l((_vm.recordings),function(recording,index){return _c('div',{key:recording.id,staticClass:"recording",class:{ hidden: !_vm.listExpanded && index >= 4 }},[_c('div',{staticClass:"title"},[_vm._v(_vm._s(recording.name)),(recording.hint)?_c('div',{staticClass:"subtitle inline-hint"},[_vm._v(_vm._s(recording.hint))]):_vm._e()]),(!recording.chapters || !recording.chapters.length)?[_c('div',{staticClass:"actions"},[(recording.downloadUrl)?_c('a',{staticClass:"action-link",attrs:{"href":recording.downloadUrl,"target":"_blank","download":"download"}},[_vm._v(_vm._s(_vm.$t('generic_form.video.recordings.download')))]):_vm._e(),(_vm.isSpotMeUser)?[(recording.vttDownloadUrl)?_c('a',{staticClass:"action-link",attrs:{"href":recording.vttDownloadUrl,"target":"_blank","download":"download"}},[_vm._v(_vm._s(_vm.$t('generic_form.video.recordings.download_transcript')))]):(recording.downloadUrl && !recording.transcriptionRequested)?_c('a',{staticClass:"action-link",on:{"click":function($event){return _vm.transcribeClicked(recording)}}},[_vm._v(_vm._s(_vm.$t('generic_form.video.recordings.generate_transcript')))]):(recording.transcriptionRequested)?_c('a',{staticClass:"action-link disabled"},[_vm._v(_vm._s(_vm.$t('generic_form.video.recordings.generating_transcript')))]):_vm._e()]:_vm._e()],2)]:[_c('div',{staticClass:"chapter-list"},_vm._l((recording.chapters),function(chapter){return _c('div',{key:chapter.id,staticClass:"chapter"},[_c('div',{staticClass:"chapter-title"},[_vm._v(_vm._s(chapter.name))]),_c('div',{staticClass:"actions"},[(chapter.downloadUrl)?_c('a',{staticClass:"action-link",attrs:{"href":chapter.downloadUrl,"target":"_blank","download":"download"}},[_vm._v(_vm._s(_vm.$t('generic_form.video.recordings.download')))]):_vm._e(),(_vm.isSpotMeUser)?[(chapter.vttDownloadUrl)?_c('a',{staticClass:"action-link",attrs:{"href":chapter.vttDownloadUrl,"target":"_blank","download":"download"}},[_vm._v(_vm._s(_vm.$t('generic_form.video.recordings.download_transcript')))]):(chapter.downloadUrl && !chapter.transcriptionRequested)?_c('a',{staticClass:"action-link",on:{"click":function($event){return _vm.transcribeClicked(chapter)}}},[_vm._v(_vm._s(_vm.$t('generic_form.video.recordings.generate_transcript')))]):(chapter.transcriptionRequested)?_c('a',{staticClass:"action-link disabled"},[_vm._v(_vm._s(_vm.$t('generic_form.video.recordings.generating_transcript')))]):_vm._e()]:_vm._e()],2)])}),0)]],2)}),0),(_vm.recordings.length > 4)?_c('a',{staticClass:"full-list-toggler",on:{"click":function($event){_vm.listExpanded = !_vm.listExpanded}}},[_vm._v(_vm._s(_vm.listExpanded ? _vm.$t('general.show_less') : _vm.$t('general.show_more'))),_c('i',{class:{ 'ic-chevron-down': !_vm.listExpanded, 'ic-chevron-up': _vm.listExpanded }})]):_vm._e()]):_vm._e()}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "VideoCallRecordings.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__